import Separator from 'components/core/Separator'
import { StyledDayPicker } from './StyledDayPicker'
import { Minus, Trash2 } from 'lucide-react'
import { SPACING } from 'constants/spacing.constants'
import Row from 'components/core/Row'
import { TimePicker } from 'components/composite/TimePicker'
import {
    changeDatePreservingTime,
    convertDateRangeToStringNoTz,
} from '../utils/datePicker.utils'
import {
    DateRangePickerValue,
    DefaultDateRangePickerValue,
} from '../types/datePicker.types'
import { Chevron } from './Chevron'
import styled from '@emotion/styled'
import { IconButton } from 'components/core/IconButton'
import Button from 'components/core/Button'
import { useIsMobile } from 'hooks/useIsMobile'

const StyledActionRow = styled(Row)`
    padding-top: ${SPACING.sm};
    padding-bottom: ${SPACING.sm};
    justify-content: flex-end;
`

const StyledIconButton = styled(IconButton)`
    padding-left: ${SPACING.p2};
    padding-right: ${SPACING.p2};
`

type CalendarWithRangeTimeProps = {
    hasConfirmButton?: boolean
    hasTime?: boolean
    value?: DateRangePickerValue
    onChange: (dateRange: DateRangePickerValue) => void
    onConfirm?: () => void
}

const emptyFunction = () => undefined

export const CalendarWithRangeTime = ({
    hasConfirmButton = true,
    hasTime,
    value,
    onChange,
    onConfirm = emptyFunction,
}: CalendarWithRangeTimeProps) => {
    const isMobile = useIsMobile()

    let valueDateRange: DefaultDateRangePickerValue
    if (value) {
        let valueFrom: Date | undefined
        if (typeof value.from === 'string') {
            const parsed = Date.parse(value.from)
            valueFrom = isNaN(parsed) ? undefined : new Date(value.from)
        } else {
            valueFrom = value.from || undefined
        }

        let valueTo: Date | undefined
        if (typeof value.to === 'string') {
            const parsed = Date.parse(value.to)
            valueTo = isNaN(parsed) ? undefined : new Date(value.to)
        } else {
            valueTo = value.to || undefined
        }

        valueDateRange = {
            from: valueFrom,
            to: valueTo,
        }
    }

    const _onClearDate = () => {
        onChange({ to: null, from: null })
    }

    /**
     * carry over the current time when a user clicks a new day
     * instead of resetting to 00:00
     */
    const _onSelect = (newDateRange: DefaultDateRangePickerValue) => {
        if (!newDateRange) return

        if (!valueDateRange) {
            onChange(convertDateRangeToStringNoTz(newDateRange))
            return
        }

        let newFromDateFull = newDateRange.from
        let newToDateFull = newDateRange.to

        if (newDateRange.from && valueDateRange.from) {
            newFromDateFull = changeDatePreservingTime(
                valueDateRange.from,
                newDateRange.from
            )
        }

        if (newDateRange.to && valueDateRange.to) {
            newToDateFull = changeDatePreservingTime(
                valueDateRange.to,
                newDateRange.to
            )
        }

        const result = {
            from: newFromDateFull,
            to: newToDateFull,
        }

        onChange(convertDateRangeToStringNoTz(result))
    }

    const _onChangeTime = (type: 'from' | 'to') => (date: Date | undefined) => {
        if (!date) return

        const newDateRange = {
            from: valueDateRange?.from,
            to: valueDateRange?.to,
        }

        newDateRange[type] = date

        onChange(convertDateRangeToStringNoTz(newDateRange))
    }

    return (
        <>
            <StyledDayPicker
                mode="range"
                // Two months do not fit on the screen for mobile.
                numberOfMonths={isMobile ? 1 : 2}
                selected={valueDateRange}
                components={{ Chevron }}
                onSelect={_onSelect}
            />
            {(hasTime || !!valueDateRange?.to || !!valueDateRange?.from) && (
                <>
                    <Separator />
                    <Row
                        p="p1"
                        fullWidth
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Row justifyContent="flex-start">
                            {hasTime && (
                                <>
                                    <TimePicker
                                        date={valueDateRange?.from}
                                        onChange={_onChangeTime('from')}
                                        Icon={Minus}
                                    />
                                    <TimePicker
                                        date={valueDateRange?.to}
                                        onChange={_onChangeTime('to')}
                                    />
                                </>
                            )}
                        </Row>

                        <StyledActionRow gap="sm">
                            {(valueDateRange?.to || valueDateRange?.from) && (
                                <StyledIconButton onClick={_onClearDate}>
                                    <Trash2 size={SPACING.p2} />
                                </StyledIconButton>
                            )}
                            {hasConfirmButton && (
                                <Button variant="secondary" onClick={onConfirm}>
                                    Confirm
                                </Button>
                            )}
                        </StyledActionRow>
                    </Row>
                </>
            )}
        </>
    )
}
